import { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Plus, Trash2, X } from 'lucide-react';
import { createCliente, deleteCliente, getCliente, updateCliente } from '../services/clienteService';
import { getAllCidades, getCidadesByUf, UF_OPTIONS } from '../services/cidadeService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import SearchAutocompleteSelect from '../components/SearchAutocompleteSelect';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCnpj, formatCpf, formatCep, isControlKey } from '../utils/formatters';
import { asArray } from '../utils/normalizers';
import useSubmitLock from '../hooks/useSubmitLock';

const mockCidades = [{ id: 1, nome: 'Sao Paulo' }, { id: 2, nome: 'Campinas' }, { id: 3, nome: 'Santos' }];
/*
FUNCIONALIDADE USUARIOS DE CLIENTE
const mockUsuariosTecnicos = [{ id: 1, nome: 'Tecnico 1' }, { id: 2, nome: 'Tecnico 2' }];
const mockUsuariosFinanceiros = [{ id: 3, nome: 'Financeiro 1' }, { id: 4, nome: 'Financeiro 2' }];
const mockUsuariosAdministrativos = [{ id: 5, nome: 'Administrativo 1' }, { id: 6, nome: 'Administrativo 2' }];
*/

const mockClientesInicial = [
  { idCliente: 1, razaoSocial: 'Cliente Alpha', numrCnpj: '11222333000181', numrCpf: '', endereco: 'Av. Brasil, 1000', codgCep: 13000000, idCidade: 2, idUsuarioTecnico: 1, idUsuarioFinanceiro: 3, idUsuarioAdministrativo: 5, criadoEm: '2026-03-01T12:00:00.000Z' },
];

const INITIAL_FORM_DATA = { razaoSocial: '', numrCnpj: '', numrCpf: '', endereco: '', codgCep: '', idCidade: '', idUsuarioTecnico: '', idUsuarioFinanceiro: '', idUsuarioAdministrativo: '' };

// Resolve o nome de um item (ex.: cidade) a partir do id; retorna '-' se não achar.
function findLabel(list, id) {
  // AJUSTE BACKEND AQUI: se a API de cidade mudar id/nome, adapte a resolucao aqui.
  const item = list.find((entry) => String(entry.id || entry.idCidade) === String(id));
  return item ? item.nome || item.nomeCidade : '-';
}

// Tela de Gestão de Clientes: CRUD com documento (CNPJ ou CPF), endereço e cidade (UF + autocomplete).
function ClientesPage() {
  const [clientes, setClientes] = useState(mockClientesInicial);
  const [selectedUf, setSelectedUf] = useState('MT');
  const [cidades, setCidades] = useState(mockCidades);
  const [cidadesTodas, setCidadesTodas] = useState(mockCidades);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClienteId, setEditingClienteId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [viewCliente, setViewCliente] = useState(null);

  // Carrega os clientes da API ao montar (cai no mock em caso de erro).
  useEffect(() => {
    let isMounted = true;
    const loadClientes = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de listagem de clientes usado nesta tela.
        const data = await getCliente();
        if (isMounted) setClientes(asArray(data));
      } catch (error) {
        if (isMounted) setClientes(mockClientesInicial);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadClientes();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Carrega todas as cidades para resolver o nome na listagem.
    const loadAllCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: carga global de cidades para resolver nome da cidade na listagem.
        const data = await getAllCidades();
        if (isMounted) {
          setCidadesTodas(Array.isArray(data) && data.length > 0 ? data : mockCidades);
        }
      } catch (error) {
        if (isMounted) {
          setCidadesTodas(mockCidades);
        }
      }
    };

    loadAllCidades();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Recarrega as cidades do formulário sempre que a UF selecionada muda.
    const loadCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de cidades por UF para o select do formulario.
        const data = await getCidadesByUf(selectedUf);
        if (isMounted) {
          setCidades(Array.isArray(data) && data.length > 0 ? data : mockCidades);
        }
      } catch (error) {
        if (isMounted) {
          setCidades(mockCidades);
        }
      }
    };

    loadCidades();

    return () => {
      isMounted = false;
    };
  }, [selectedUf]);

  const filteredClientes = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return clientes;

    return clientes.filter((cliente) => {
      const razao = cliente.razaoSocial || '';
      const cnpj = cliente.numrCnpj || '';
      const cpf = cliente.numrCpf || '';
      const endereco = cliente.endereco || '';
      return (
        razao.toLowerCase().includes(normalized) ||
        cnpj.toLowerCase().includes(normalized) ||
        cpf.toLowerCase().includes(normalized) ||
        endereco.toLowerCase().includes(normalized)
      );
    });
  }, [clientes, query]);

  const stats = useMemo(() => {
    const total = clientes.length;
    const comCnpj = clientes.filter((cliente) => onlyDigits(cliente.numrCnpj).length === 14).length;
    const comCpf = clientes.filter((cliente) => onlyDigits(cliente.numrCpf).length === 11).length;
    return { total, comCnpj, comCpf };
  }, [clientes]);

  // Handler de onChange por campo do formulário.
  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Handler de onChange que aplica uma máscara (CNPJ/CPF/CEP) antes de salvar.
  const handleMaskedChange = (field, formatter) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: formatter(event.target.value) }));
  };

  // Abre o modal em modo de criação (reseta formulário e UF padrão MT).
  const openModal = () => {
    setFormErrors({});
    setEditingClienteId(null);
    setSelectedUf('MT');
    setFormData(INITIAL_FORM_DATA);
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com máscaras aplicadas.
  const openEditModal = (cliente) => {
    setFormErrors({});
    setEditingClienteId(cliente.idCliente);
    setSelectedUf((cliente.uf || cliente.UF || 'MT').toUpperCase());
    setFormData({
      razaoSocial: cliente.razaoSocial || '',
      numrCnpj: formatCnpj(cliente.numrCnpj || ''),
      numrCpf: formatCpf(cliente.numrCpf || ''),
      endereco: cliente.endereco || '',
      codgCep: formatCep(String(cliente.codgCep || '')),
      idCidade: cliente.idCidade || '',
      idUsuarioTecnico: cliente.idUsuarioTecnico || '',
      idUsuarioFinanceiro: cliente.idUsuarioFinanceiro || '',
      idUsuarioAdministrativo: cliente.idUsuarioAdministrativo || '',
    });
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClienteId(null);
    setFormErrors({});
  };

  // Valida: razão social obrigatória, exatamente um documento (CNPJ ou CPF) válido e CEP; retorna erros + dígitos.
  const validateForm = () => {
    const errors = {};
    if (!formData.razaoSocial.trim()) errors.razaoSocial = 'Razão social obrigatória.';

    const cnpjDigits = onlyDigits(formData.numrCnpj);
    const cpfDigits = onlyDigits(formData.numrCpf);
    const cepDigits = onlyDigits(formData.codgCep);

    if (!cnpjDigits && !cpfDigits) {
      errors.numrCnpj = 'Informe CNPJ ou CPF.';
      errors.numrCpf = 'Informe CNPJ ou CPF.';
    }

    if (cnpjDigits && cpfDigits) {
      errors.numrCnpj = 'Informe apenas um CNPJ ou um CPF.';
      errors.numrCpf = 'Informe apenas um CNPJ ou um CPF.';
    }

    if (formData.numrCnpj.trim() && cnpjDigits.length !== 14) errors.numrCnpj = 'CNPJ inválido.';
    if (formData.numrCpf.trim() && cpfDigits.length !== 11) errors.numrCpf = 'CPF inválido.';
    if (cepDigits && cepDigits.length !== 8) errors.codgCep = 'CEP inválido.';

    return { errors, cnpjDigits, cpfDigits };
  };

  // Submete o form: valida, checa documento duplicado, cria/atualiza na API e refaz a lista.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();
    const { errors, cnpjDigits, cpfDigits } = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const duplicate = clientes.some((cliente) => {
      if (editingClienteId && cliente.idCliente === editingClienteId) return false;
      const clienteCnpj = onlyDigits(cliente.numrCnpj);
      const clienteCpf = onlyDigits(cliente.numrCpf);
      return (cnpjDigits && clienteCnpj === cnpjDigits) || (cpfDigits && clienteCpf === cpfDigits);
    });

    if (duplicate) {
      setFormErrors({ numrCnpj: 'Documento já cadastrado.' });
      return;
    }

    try {
      if (editingClienteId) {
        // AJUSTE BACKEND AQUI: fluxo de update de cliente.
        const { payload } = await updateCliente(editingClienteId, formData);
        setClientes((prev) =>
          prev.map((cliente) => (cliente.idCliente === editingClienteId ? { ...cliente, ...payload } : cliente))
        );
        showSuccessToast('Cliente atualizado com sucesso.');
      } else {
        // AJUSTE BACKEND AQUI: fluxo de criacao de cliente.
        const { response: novo, payload } = await createCliente(formData);

        const normalizedNovo = novo || { idCliente: `cli-${Date.now()}`, ...payload, criadoEm: new Date().toISOString() };
        setClientes((prev) => [normalizedNovo, ...prev]);
        showSuccessToast('Cliente cadastrado com sucesso.');
      }

      closeModal();
    } catch (error) {
      setFormErrors({ geral: error.message || 'Não foi possível salvar o cliente.' });
    }
  });

  // Exclui o cliente após confirmação e remove da lista local.
  const handleDelete = async (idCliente) => {
    const confirm = window.confirm("Deseja realmente excluir este cliente?");
    if (!confirm) return;

    try {
      await deleteCliente(idCliente);
      setClientes((prev) => prev.filter((cliente) => cliente.idCliente !== idCliente));
      showSuccessToast('Cliente excluído com sucesso.');
    } catch (error) {
      showErrorToast(error.message || 'Não foi possível excluir o cliente.');
    }
  };

  return (
    <div className="clientes-page space-y-6">
      <section className="clientes-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de clientes</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">Cadastre e gerencie os clientes do sistema.</p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Novo cliente
        </button>
      </section>

      <section className="clientes-metrics grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com CNPJ</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comCnpj}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com CPF</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comCpf}</p>
        </div>
      </section>

      <section className="clientes-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por razão social, CNPJ, CPF ou endereço"
            resultCount={filteredClientes.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Razão social</th>
                <th className="px-4 py-3">CNPJ</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Cidade</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredClientes.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 5 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredClientes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              ) : (
                filteredClientes.map((cliente) => (
                  <tr key={cliente.idCliente} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 font-medium text-slate-900">{cliente.razaoSocial}</td>
                    <td className="px-4 py-4 text-slate-600">{formatCnpj(cliente.numrCnpj || '') || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatCpf(cliente.numrCpf || '') || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(cidadesTodas, cliente.idCidade)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewCliente(cliente)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(cliente)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(cliente.idCliente)}
                          className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-skeleton-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredClientes.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhum cliente encontrado.
            </div>
          ) : (
            filteredClientes.map((cliente) => (
              <div key={cliente.idCliente} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-base font-semibold text-slate-900">{cliente.razaoSocial}</p>
                <p className="text-xs text-slate-500">
                  {formatCnpj(cliente.numrCnpj || '') || formatCpf(cliente.numrCpf || '') || 'Sem documento'}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span>Cidade: {findLabel(cidadesTodas, cliente.idCidade)}</span>
                  <span className="col-span-2">Endereço: {cliente.endereco || '-'}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewCliente(cliente)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(cliente)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(cliente.idCliente)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewCliente}
        onClose={() => setViewCliente(null)}
        title="Detalhes do cliente"
        sections={viewCliente ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Razão social', value: viewCliente.razaoSocial || '-' },
              { label: 'CNPJ', value: formatCnpj(viewCliente.numrCnpj || '') || '-' },
              { label: 'CPF', value: formatCpf(viewCliente.numrCpf || '') || '-' },
            ],
          },
          {
            title: 'Endereço',
            fields: [
              { label: 'Endereço', value: viewCliente.endereco || '-' },
              { label: 'CEP', value: formatCep(String(viewCliente.codgCep || '')) || '-' },
              { label: 'Cidade', value: findLabel(cidadesTodas, viewCliente.idCidade) },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSave}
            className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingClienteId ? 'Editar cliente' : 'Cadastrar cliente'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium text-slate-700">Razão social <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={formData.razaoSocial}
                  onChange={handleFieldChange('razaoSocial')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.razaoSocial ? <p className="text-xs text-rose-600">{formErrors.razaoSocial}</p> : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">CNPJ <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={formData.numrCnpj}
                  onChange={handleMaskedChange('numrCnpj', formatCnpj)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.numrCnpj ? <p className="text-xs text-rose-600">{formErrors.numrCnpj}</p> : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">CPF <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={formData.numrCpf}
                  onChange={handleMaskedChange('numrCpf', formatCpf)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.numrCpf ? <p className="text-xs text-rose-600">{formErrors.numrCpf}</p> : null}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium text-slate-700">Endereço</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={handleFieldChange('endereco')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
              </div>

              <div className="sm:col-span-2 grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">CEP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.codgCep}
                    onChange={handleMaskedChange('codgCep', formatCep)}
                    onKeyDown={(event) => {
                      if (isControlKey(event)) return;
                      if (event.key.length === 1 && !/\d/.test(event.key)) event.preventDefault();
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                  />
                  {formErrors.codgCep ? <p className="text-xs text-rose-600">{formErrors.codgCep}</p> : null}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">UF</label>
                  <select
                    value={selectedUf}
                    onChange={(event) => {
                      const uf = event.target.value;
                      setSelectedUf(uf);
                      setFormData((prev) => ({ ...prev, idCidade: '' }));
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                  >
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Cidade</label>
                  <SearchAutocompleteSelect
                    value={formData.idCidade}
                    onChange={handleFieldChange('idCidade')}
                    placeholder="Buscar cidade"
                    options={cidades.map((cidade) => ({
                      id: cidade.idCidade || cidade.id,
                      label: cidade.nomeCidade || cidade.nome,
                    }))}
                  />
                </div>
              </div>

              {/*
              FUNCIONALIDADE USUARIOS DE CLIENTE
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Usuário técnico</label>
                <select
                  value={formData.id_usuario_tecnico}
                  onChange={handleFieldChange('id_usuario_tecnico')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                >
                  <option value="">Selecione</option>
                  {mockUsuariosTecnicos.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>{usuario.nome}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Usuário financeiro</label>
                <select
                  value={formData.id_usuario_financeiro}
                  onChange={handleFieldChange('id_usuario_financeiro')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                >
                  <option value="">Selecione</option>
                  {mockUsuariosFinanceiros.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>{usuario.nome}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Usuário administrativo</label>
                <select
                  value={formData.id_usuario_administrativo}
                  onChange={handleFieldChange('id_usuario_administrativo')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                >
                  <option value="">Selecione</option>
                  {mockUsuariosAdministrativos.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>{usuario.nome}</option>
                  ))}
                </select>
              </div>
              */}
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? 'Salvando...' : editingClienteId ? 'Salvar alterações' : 'Salvar cliente'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default ClientesPage;
